import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { bookingAPI, userAPI, companyAPI, squareAPI } from '../services/api';
import Layout from '../components/Layout';
import Icon from '../components/Icon';


// Status colors
const STATUS_COLORS = {
  pending: { bg: 'rgba(245, 158, 11, 0.15)', border: '#f59e0b', text: '#fbbf24' },
  confirmed: { bg: 'rgba(59, 130, 246, 0.15)', border: '#3b82f6', text: '#60a5fa' },
  in_progress: { bg: 'rgba(78, 205, 196, 0.15)', border: '#4ecdc4', text: '#4ecdc4' },
  completed: { bg: 'rgba(26, 40, 17, 0.15)', border: '#1A2811', text: '#4A8C18' },
  cancelled: { bg: 'rgba(239, 68, 68, 0.15)', border: '#ef4444', text: '#f87171' },
};

function TechnicianDashboard({ user, onLogout, darkMode = true, onToggleDarkMode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [bookings, setBookings] = useState([]);

  // Sync activeTab with URL query params when they change
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') || 'overview';
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);
  const [company, setCompany] = useState(null);
  const [technicianProfile, setTechnicianProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [completedBookingsList, setCompletedBookingsList] = useState([]);
  const [completionNotes, setCompletionNotes] = useState('');
  const [showCompleteConfirmation, setShowCompleteConfirmation] = useState(false);
  const [draftParts, setDraftParts] = useState([{ item_name: '', quantity: 1, unit_price: 0, variation_id: '', catalog_object_id: '' }]);
  const [savingDraft, setSavingDraft] = useState(false);
  const [squareCatalogItems, setSquareCatalogItems] = useState([]);
  const [squareInventory, setSquareInventory] = useState({});
  const [squareCatalogLoading, setSquareCatalogLoading] = useState(false);
  const [squareCatalogError, setSquareCatalogError] = useState('');
  const [catalogSearchTerm, setCatalogSearchTerm] = useState('');
  
  // Profile form
  const [profileData, setProfileData] = useState({
    firstname: user?.firstname || '',
    surname: user?.surname || '',
    email: user?.email || '',
    phone: '',
    signature: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Time Off state
  const [timeOffList, setTimeOffList] = useState([]);
  const [loadingTimeOff, setLoadingTimeOff] = useState(false);
  const [timeOffForm, setTimeOffForm] = useState({
    start_date: '',
    end_date: '',
    reason: 'vacation'
  });
  const [savingTimeOff, setSavingTimeOff] = useState(false);
  // Delegation state
  const [showDelegationForm, setShowDelegationForm] = useState(false);
  const [bookingsToDelegate, setBookingsToDelegate] = useState([]); // each item: { booking, available_technicians }
  const [delegationAssignments, setDelegationAssignments] = useState({}); // { bookingId: technicianId }
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  // Reschedule when no one available
  const [rescheduleForItem, setRescheduleForItem] = useState(null);
  const [alternativeSlots, setAlternativeSlots] = useState([]);
  const [loadingAlternativeSlots, setLoadingAlternativeSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  
  // Quick add booking state (for clicking empty calendar slots)
  const [showQuickBookModal, setShowQuickBookModal] = useState(false);
  const [quickBookSlot, setQuickBookSlot] = useState(null); // { date: Date, time: string }

  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  function getWeekDays(weekStart) {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  }

  useEffect(() => {
    fetchData();
  }, [currentWeekStart]);
  
  useEffect(() => {
    if (activeTab === 'timeoff') {
      fetchTimeOff();
    }
  }, [activeTab]);
  
  const fetchTimeOff = async () => {
    try {
      setLoadingTimeOff(true);
      const response = await bookingAPI.getMyTimeOff();
      setTimeOffList(response.data.time_off || []);
    } catch (err) {
      console.log('Could not fetch time off');
    } finally {
      setLoadingTimeOff(false);
    }
  };
  
  const checkBookingsForTimeOff = async () => {
    if (!timeOffForm.start_date || !timeOffForm.end_date) {
      setError('Please select start and end dates');
      return;
    }
    
    // Validate dates
    const startDate = new Date(timeOffForm.start_date);
    const endDate = new Date(timeOffForm.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (endDate < startDate) {
      setError('End date cannot be before start date');
      return;
    }
    
    if (startDate < today) {
      setError('Start date cannot be in the past');
      return;
    }
    
    try {
      setCheckingAvailability(true);
      setError('');
      
      // Check for bookings that need delegation
      const response = await bookingAPI.getBookingsToDelegate({
        start_date: timeOffForm.start_date,
        end_date: timeOffForm.end_date
      });
      
      const bookingsData = response.data.bookings || []; // each item: { booking, available_technicians }
      
      if (bookingsData.length > 0) {
        setBookingsToDelegate(bookingsData);
        setShowDelegationForm(true);
        const initialAssignments = {};
        bookingsData.forEach(item => {
          const techs = item.available_technicians || [];
          if (techs.length > 0) {
            initialAssignments[item.booking.id] = techs[0].id;
          }
        });
        setDelegationAssignments(initialAssignments);
      } else {
        // No bookings to delegate, proceed directly
        await submitTimeOff([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to check availability');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const submitTimeOff = async (delegations) => {
    try {
      setSavingTimeOff(true);
      setError('');
      
      const timeOffData = {
        ...timeOffForm,
        delegations: delegations
      };
      
      await bookingAPI.addMyTimeOff(timeOffData);
      setSuccess(delegations.length > 0 
        ? 'Time off added! Delegation requests sent to colleagues.'
        : 'Time off added successfully!');
      setTimeOffForm({ start_date: '', end_date: '', reason: 'vacation' });
      setShowDelegationForm(false);
      setBookingsToDelegate([]);
      setDelegationAssignments({});
      fetchTimeOff();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add time off');
    } finally {
      setSavingTimeOff(false);
    }
  };

  const handleSubmitWithDelegations = () => {
    if (bookingsToDelegate.length === 0) {
      submitTimeOff([]);
      return;
    }
    const delegations = Object.entries(delegationAssignments)
      .filter(([, techId]) => techId)
      .map(([bookingId, techId]) => ({
        booking_id: parseInt(bookingId),
        to_technician_id: parseInt(techId)
      }));
    const unassigned = bookingsToDelegate.filter(item => !delegationAssignments[item.booking.id]);
    if (unassigned.length > 0) {
      setError(`Assign all ${unassigned.length} booking(s) to a colleague, or use "Suggest alternative date" for those with no available colleagues.`);
      return;
    }
    submitTimeOff(delegations);
  };

  const handleSuggestAlternativeDate = async (item) => {
    setRescheduleForItem(item);
    setAlternativeSlots([]);
    setSelectedSlot(null);
    setShowRescheduleModal(true);
    setLoadingAlternativeSlots(true);
    setError('');
    try {
      const res = await bookingAPI.getBookingAlternativeSlots(item.booking.id);
      setAlternativeSlots(res.data.slots || []);
      if (!(res.data.slots || []).length) {
        setError('No alternative slots found in the next 14 days.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load alternative slots');
    } finally {
      setLoadingAlternativeSlots(false);
    }
  };

  const handleConfirmReschedule = async () => {
    if (!rescheduleForItem || !selectedSlot) return;
    setRescheduling(true);
    setError('');
    try {
      await bookingAPI.rescheduleBookingAsTechnician(rescheduleForItem.booking.id, {
        booking_date: selectedSlot.date,
        start_time: selectedSlot.start_time,
        technician_id: selectedSlot.technician_id || undefined
      });
      setBookingsToDelegate(prev => prev.filter(i => i.booking.id !== rescheduleForItem.booking.id));
      setDelegationAssignments(prev => {
        const next = { ...prev };
        delete next[rescheduleForItem.booking.id];
        return next;
      });
      setSuccess('Booking rescheduled. The customer has been notified by email with the new date/time and bike shop contact.');
      setTimeout(() => setSuccess(''), 8000);
      setShowRescheduleModal(false);
      setRescheduleForItem(null);
      setAlternativeSlots([]);
      setSelectedSlot(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reschedule booking');
    } finally {
      setRescheduling(false);
    }
  };

  const handleAddTimeOff = async (e) => {
    e.preventDefault();
    await checkBookingsForTimeOff();
  };
  
  const handleDeleteTimeOff = async (timeOffId) => {
    if (!window.confirm('Are you sure you want to delete this time off entry?')) return;
    
    try {
      setError('');
      await bookingAPI.deleteMyTimeOff(timeOffId);
      setSuccess('Time off deleted successfully!');
      fetchTimeOff();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete time off');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      // Get company info
      try {
        const companyResp = await companyAPI.getCompanyInfo();
        setCompany(companyResp.data.company);
      } catch (err) {
        console.log('Could not fetch company info');
      }

      // Get technician profile
      try {
        const profileResp = await bookingAPI.getTechnicianProfile();
        const tech = profileResp.data.technician;
        setTechnicianProfile(tech);
        // Update profile form with technician data
        if (tech) {
          setProfileData(prev => ({
            ...prev,
            phone: tech.phone || '',
            signature: tech.signature || ''
          }));
        }
      } catch (err) {
        console.log('Could not fetch technician profile');
      }

      // Get technician's bookings
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      try {
        const bookingsResp = await bookingAPI.getTechnicianBookings({
          date_from: currentWeekStart.toISOString().split('T')[0],
          date_to: weekEnd.toISOString().split('T')[0],
        });
        setBookings(bookingsResp.data.bookings || []);
      } catch (err) {
        console.log('Could not fetch bookings');
        setBookings([]);
      }

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentWeekStart(getWeekStart(newDate));
  };

  const goToToday = () => {
    setCurrentWeekStart(getWeekStart(new Date()));
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const getBookingsForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookings.filter(b => b.booking_date === dateStr);
  };

  const handleUpdateBookingStatus = async (booking, newStatus) => {
    try {
      setUpdatingStatus(true);
      setError('');
      
      const updateData = { status: newStatus };
      if (newStatus === 'completed' && completionNotes.trim()) {
        updateData.notes = completionNotes.trim();
      }
      
      const response = await bookingAPI.updateTechnicianBookingStatus(booking.id, updateData);
      
      // Show success message with email notification status
      if (newStatus === 'completed') {
        const emailSent = response.data?.email_sent;
        setSuccess(emailSent 
          ? 'Service completed! Customer has been notified by email.' 
          : 'Service completed!');
      } else {
        setSuccess('Booking started!');
      }
      
      // Keep modal open when starting work to show service details
      // Only close when completing the booking
      if (newStatus === 'completed') {
        setShowBookingModal(false);
        setSelectedBooking(null);
        setCompletionNotes(''); // Clear notes after completion
        setShowCompleteConfirmation(false);
      } else {
        // Update the booking status in the modal without closing it
        setSelectedBooking({ ...booking, status: newStatus });
      }
      
      fetchData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update booking status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!selectedBooking) return;
    const items = draftParts
      .map(p => ({
        item_name: (p.item_name || '').trim(),
        quantity: Math.max(0, Number(p.quantity) || 0),
        unit_price: Math.max(0, Number(p.unit_price) || 0),
        variation_id: (p.variation_id || '').trim() || undefined,
        catalog_object_id: (p.catalog_object_id || '').trim() || undefined,
      }))
      .filter(p => p.item_name);
    try {
      setSavingDraft(true);
      setError('');
      await bookingAPI.completeTechnicianBookingDraft(selectedBooking.id, {
        notes: completionNotes.trim() || undefined,
        items: items.length ? items : undefined
      });
      setSuccess('Draft saved. Company admin will complete invoicing.');
      setShowBookingModal(false);
      setSelectedBooking(null);
      setCompletionNotes('');
      setDraftParts([{ item_name: '', quantity: 1, unit_price: 0, variation_id: '', catalog_object_id: '' }]);
      fetchData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const openBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setShowBookingModal(true);
    setDraftParts([{ item_name: '', quantity: 1, unit_price: 0, variation_id: '', catalog_object_id: '' }]);
    setCatalogSearchTerm('');
    setSquareCatalogError('');
  };

  // Load Square catalog + inventory for technician when modal opens (pending/confirmed)
  useEffect(() => {
    if (!showBookingModal || user?.role !== 'technician' || !selectedBooking) return;
    const status = selectedBooking.status;
    if (status !== 'pending' && status !== 'confirmed') return;

    let cancelled = false;
    setSquareCatalogLoading(true);
    setSquareCatalogError('');
    squareAPI.getCatalogBundle(null, false)
      .then((res) => {
        if (cancelled) return;
        const catalog = res.data?.catalog || {};
        const items = catalog.items || [];
        const inv = res.data?.inventory_counts || {};
        setSquareCatalogItems(items);
        setSquareInventory(inv);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err.response?.data?.error || 'Square katalógus nem elérhető.';
        setSquareCatalogError(msg);
        setSquareCatalogItems([]);
        setSquareInventory({});
      })
      .finally(() => {
        if (!cancelled) setSquareCatalogLoading(false);
      });
    return () => { cancelled = true; };
  }, [showBookingModal, selectedBooking?.id, user?.role, selectedBooking?.status]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSavingProfile(true);

    try {
      // Update user profile (name, email)
      await userAPI.updateProfile({
        firstname: profileData.firstname,
        surname: profileData.surname,
        email: profileData.email
      });
      
      // Update technician profile (phone, signature)
      await bookingAPI.updateTechnicianProfile({
        phone: profileData.phone,
        signature: profileData.signature
      });
      
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    setSavingPassword(true);

    try {
      await userAPI.updateProfile({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      setSuccess('Password changed successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const weekDays = getWeekDays(currentWeekStart);
  const timeSlots = [];
  for (let hour = 8; hour <= 18; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
  }

  const profileInputStyle = {
    width: '100%',
    padding: '12px',
    borderRadius: '10px',
    border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #d1d5db',
    background: darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
    color: darkMode ? '#ffffff' : '#111827',
    fontSize: '14px'
  };
  const profileLabelStyle = { color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151' };

  // Calculate stats
  const todayBookings = getBookingsForDay(new Date());
  const pendingCount = bookings.filter(b => b.status === 'pending' || b.status === 'confirmed').length;
  const inProgressCount = bookings.filter(b => b.status === 'in_progress').length;
  const completedCount = bookings.filter(b => b.status === 'completed').length;

  return (
    <Layout
      user={user}
      onLogout={onLogout}
      pageTitle="Technician Dashboard"
      darkMode={darkMode}
      onToggleDarkMode={onToggleDarkMode}
      pageSubtitle={company ? company.name : 'Welcome'}
    >
      <div className="dashboard-shell">
        {error && <div className="error" style={{ marginBottom: '20px' }}>{error}</div>}
        {success && <div className="success" style={{ marginBottom: '20px' }}>{success}</div>}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {/* Stats Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <div className="card" style={{ background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.05) 100%)', borderLeft: '4px solid #f59e0b' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#fbbf24' }}>{pendingCount}</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Pending/Confirmed</div>
              </div>
              <div className="card" style={{ background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.15) 0%, rgba(78, 205, 196, 0.05) 100%)', borderLeft: '4px solid #4ecdc4' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#4ecdc4' }}>{inProgressCount}</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>In Progress</div>
              </div>
              <div 
                className="card" 
                style={{ 
                  background: 'linear-gradient(135deg, rgba(61, 90, 32, 0.15) 0%, rgba(61, 90, 32, 0.05) 100%)', 
                  borderLeft: '4px solid #3d5a20',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={() => {
                  // Show completed bookings modal
                  const completedBookings = bookings.filter(b => {
                    const bookingDate = new Date(b.booking_date);
                    const weekStart = new Date(currentWeekStart);
                    const weekEnd = new Date(weekStart);
                    weekEnd.setDate(weekEnd.getDate() + 7);
                    return b.status === 'completed' && bookingDate >= weekStart && bookingDate < weekEnd;
                  });
                  setSelectedBooking(null);
                  setShowCompletedModal(true);
                  setCompletedBookingsList(completedBookings);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(61, 90, 32, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#4A8C18' }}>{completedCount}</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Completed This Week</div>
              </div>
              <div className="card" style={{ background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(102, 126, 234, 0.05) 100%)', borderLeft: '4px solid #667eea' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#667eea' }}>{todayBookings.length}</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Today's Jobs</div>
              </div>
            </div>

            {/* Today's Schedule */}
            <div className="card">
              <h2 style={{ fontSize: '18px', marginBottom: '20px', color: '#ffffff' }}>📋 Today's Schedule</h2>
              
              {loading ? (
                <div className="loading">Loading...</div>
              ) : todayBookings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255,255,255,0.5)' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <Icon name="celebration" color="brand" darkMode={true} size={48} />
                  </div>
                  <p style={{ fontSize: '16px' }}>No appointments scheduled for today!</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {todayBookings.sort((a, b) => a.start_time?.localeCompare(b.start_time)).map(booking => {
                    const statusColors = STATUS_COLORS[booking.status] || STATUS_COLORS.pending;
                    return (
                      <div 
                        key={booking.id} 
                        onClick={() => openBookingDetails(booking)}
                        style={{
                          padding: '20px',
                          background: statusColors.bg,
                          borderLeft: `4px solid ${statusColors.border}`,
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'transform 0.2s, box-shadow 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateX(4px)';
                          e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateX(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                          <div>
                            <div style={{ fontWeight: '700', fontSize: '18px', color: '#ffffff', marginBottom: '4px' }}>
                              {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                            </div>
                            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '15px', marginBottom: '8px' }}>
                              {booking.service_template?.name || 'Service'}
                            </div>
                            {booking.bike && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.6)' }}>
                                <span>🚲</span>
                                <span>{booking.bike.brand} {booking.bike.model}</span>
                                <span style={{ fontSize: '12px', opacity: 0.7 }}>Frame: {booking.bike.vin}</span>
                              </div>
                            )}
                            {booking.user && (
                              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginTop: '4px' }}>
                                👤 {booking.user.firstname} {booking.user.surname}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                            <span style={{
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: '600',
                              background: 'rgba(0,0,0,0.2)',
                              color: darkMode ? statusColors.text : '#000000',
                              textTransform: 'uppercase'
                            }}>
                              {booking.status?.replace('_', ' ')}
                            </span>
                            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                              Click for actions
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Technician Info */}
            {technicianProfile && (
              <div className="card" style={{ marginTop: '24px' }}>
                <h2 style={{ fontSize: '18px', marginBottom: '16px', color: '#ffffff' }}>👨‍🔧 My Profile</h2>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <div style={{ color: 'rgba(255,255,255,0.6)' }}>
                    <strong style={{ color: '#ffffff' }}>Name:</strong> {technicianProfile.name}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.6)' }}>
                    <strong style={{ color: '#ffffff' }}>Email:</strong> {technicianProfile.email || 'Not set'}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.6)' }}>
                    <strong style={{ color: '#ffffff' }}>Phone:</strong> {technicianProfile.phone || 'Not set'}
                  </div>
                  {technicianProfile.specializations?.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <strong style={{ color: '#ffffff' }}>Specializations:</strong>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                        {technicianProfile.specializations.map((spec, idx) => (
                          <span key={idx} style={{
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            background: 'rgba(61, 90, 32, 0.1)',
                            color: '#4A8C18',
                            border: '1px solid rgba(61, 90, 32, 0.2)'
                          }}>
                            {spec}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
        <div className="card">
          <h2 style={{ fontSize: '18px', marginBottom: '20px', color: '#ffffff' }}>📅 My Service Schedule</h2>

          {/* Navigation */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '24px',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={() => navigateWeek(-1)} className="btn btn-secondary" style={{ padding: '8px 12px', fontSize: '13px' }}>
                ← Previous
              </button>
              <button onClick={goToToday} className="btn btn-primary" style={{ padding: '8px 12px', fontSize: '13px' }}>
                Ma
              </button>
              <button onClick={() => navigateWeek(1)} className="btn btn-secondary" style={{ padding: '8px 12px', fontSize: '13px' }}>
                Következő →
              </button>
            </div>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
              {formatDate(currentWeekStart)} - {formatDate(weekDays[6])}
            </span>
          </div>

          {/* Legend */}
          <div style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '20px',
            flexWrap: 'wrap',
            padding: '10px 14px',
            background: 'var(--bg-overlay-light)',
            borderRadius: '12px'
          }}>
            {Object.entries(STATUS_COLORS).map(([status, colors]) => (
              <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '3px',
                  background: colors.bg,
                  border: `2px solid ${colors.border}`,
                  flexShrink: 0
                }} />
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                  {status.replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>

          {loading ? (
            <div className="loading">Loading schedule...</div>
          ) : (
            <div style={{
              background: 'var(--bg-overlay-light)',
              border: '1px solid rgba(255, 255, 255, 0.06)',
              borderRadius: '12px',
              overflow: 'auto'
            }}>
              {/* Days header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '80px repeat(7, 1fr)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                minWidth: '600px'
              }}>
                <div style={{ 
                  padding: '12px', 
                  background: 'rgba(255,255,255,0.02)', 
                  fontWeight: '600', 
                  textAlign: 'center',
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: '11px',
                  textTransform: 'uppercase'
                }}>
                  Time
                </div>
                {weekDays.map((day, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      background: isToday(day) ? 'linear-gradient(135deg, rgba(61, 90, 32, 0.15) 0%, rgba(78, 205, 196, 0.15) 100%)' : 'rgba(255,255,255,0.02)',
                      borderLeft: '1px solid rgba(255,255,255,0.06)'
                    }}
                  >
                    <div style={{ fontSize: '11px', color: isToday(day) ? 'var(--lime-light)' : 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>
                      {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: isToday(day) ? 'white' : 'rgba(255,255,255,0.8)', marginTop: '4px' }}>
                      {day.getDate()}
                    </div>
                  </div>
                ))}
              </div>

              {/* Time slots grid */}
              <div style={{ maxHeight: '500px', overflowY: 'auto', minWidth: '600px' }}>
                {timeSlots.map((timeSlot) => (
                  <div
                    key={timeSlot}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '80px repeat(7, 1fr)',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.03)',
                      minHeight: '50px'
                    }}
                  >
                    <div style={{
                      padding: '8px',
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.4)',
                      textAlign: 'center',
                      background: 'rgba(255,255,255,0.01)'
                    }}>
                      {timeSlot}
                    </div>
                    {weekDays.map((day, dayIdx) => {
                      const dayBookings = getBookingsForDay(day);
                      const slotBookings = dayBookings.filter(b => {
                        const bookingHour = parseInt(b.start_time?.substring(0, 2) || '0');
                        const slotHour = parseInt(timeSlot.substring(0, 2));
                        return bookingHour === slotHour;
                      });

                      // Check if this slot is in the past
                      const slotDateTime = new Date(day);
                      slotDateTime.setHours(parseInt(timeSlot.substring(0, 2)), 0, 0, 0);
                      const isPast = slotDateTime < new Date();
                      
                      return (
                        <div
                          key={dayIdx}
                          onClick={() => {
                            // Only allow clicking on empty, future slots
                            if (slotBookings.length === 0 && !isPast) {
                              setQuickBookSlot({ date: day, time: timeSlot });
                              setShowQuickBookModal(true);
                            }
                          }}
                          style={{
                            padding: '4px',
                            borderRight: dayIdx < 6 ? '1px solid rgba(255,255,255,0.03)' : 'none',
                            background: isToday(day) ? 'rgba(102, 126, 234, 0.03)' : 'transparent',
                            cursor: slotBookings.length === 0 && !isPast ? 'pointer' : 'default',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (slotBookings.length === 0 && !isPast) {
                              e.currentTarget.style.background = 'rgba(61, 90, 32, 0.08)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = isToday(day) ? 'rgba(102, 126, 234, 0.03)' : 'transparent';
                          }}
                        >
                          {slotBookings.map((booking) => {
                            const statusColors = STATUS_COLORS[booking.status] || STATUS_COLORS.pending;
                            return (
                              <div
                                key={booking.id}
                                onClick={() => openBookingDetails(booking)}
                                style={{
                                  padding: '6px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  background: statusColors.bg,
                                  borderLeft: `3px solid ${statusColors.border}`,
                                  color: darkMode ? statusColors.text : '#000000',
                                  marginBottom: '4px',
                                  cursor: 'pointer',
                                  transition: 'transform 0.1s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                              >
                                <div style={{ fontWeight: '600' }}>
                                  {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                                </div>
                                <div style={{ opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {booking.service_template?.name || 'Service'}
                                </div>
                                {booking.bike && (
                                  <div style={{ opacity: 0.6, fontSize: '10px' }}>
                                    {booking.bike.brand} {booking.bike.model}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Time Off Tab */}
        {activeTab === 'timeoff' && (
          <div className="card">
            <h2 style={{ fontSize: '18px', marginBottom: '24px', color: '#ffffff' }}>🏖️ My Time Off</h2>
            
            <div style={{
              background: 'var(--bg-overlay-light)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              border: '1px solid rgba(255,255,255,0.06)'
            }}>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>
                Set your vacation days or time off here. The system will automatically avoid assigning you bookings during these periods.
              </p>
            </div>
            
            {/* Add Time Off Form */}
            <form onSubmit={handleAddTimeOff} style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px' }}>Add Time Off</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                <div className="form-group">
                  <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Start Date</label>
                  <input
                    type="date"
                    value={timeOffForm.start_date}
                    onChange={(e) => setTimeOffForm({...timeOffForm, start_date: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div className="form-group">
                  <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>End Date</label>
                  <input
                    type="date"
                    value={timeOffForm.end_date}
                    onChange={(e) => setTimeOffForm({...timeOffForm, end_date: e.target.value})}
                    min={timeOffForm.start_date || new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div className="form-group">
                  <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Reason</label>
                  <select
                    value={timeOffForm.reason}
                    onChange={(e) => setTimeOffForm({...timeOffForm, reason: e.target.value})}
                  >
                    <option value="vacation">🏖️ Vacation</option>
                    <option value="sick">🤒 Sick Leave</option>
                    <option value="personal">👤 Personal</option>
                    <option value="training">📚 Training</option>
                    <option value="other">📝 Other</option>
                  </select>
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={savingTimeOff || checkingAvailability} style={{ marginTop: '12px' }}>
                {checkingAvailability ? 'Checking...' : savingTimeOff ? 'Adding...' : '+ Add Time Off'}
              </button>
            </form>
            
            {/* Delegation Form Modal */}
            {showDelegationForm && bookingsToDelegate.length > 0 && (
              <div style={{
                background: 'rgba(245, 158, 11, 0.1)',
                border: '2px solid rgba(245, 158, 11, 0.4)',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <Icon name="warning" customColor="#f59e0b" size={28} />
                  <div>
                    <h3 style={{ color: '#f59e0b', fontSize: '16px', margin: 0 }}>
                      You have {bookingsToDelegate.length} booking(s) during this period
                    </h3>
                    <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', margin: '4px 0 0' }}>
                      Please delegate these bookings to a colleague before taking time off.
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                  {bookingsToDelegate.map(item => {
                    const b = item.booking;
                    const techs = item.available_technicians || [];
                    const customerName = b.user ? [b.user.firstname, b.user.surname].filter(Boolean).join(' ') || b.user.username : 'Customer';
                    const bikeInfo = b.bike ? `${b.bike.brand || ''} ${b.bike.model || ''}`.trim() || 'Bike' : 'Bike';
                    const serviceName = b.service_template?.name || b.service_template_name || '';
                    const canDelegate = techs.length > 0;
                    return (
                      <div key={b.id} style={{
                        background: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.1)',
                        borderRadius: '10px',
                        padding: '14px',
                        border: '1px solid rgba(255,255,255,0.08)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                          <div style={{ flex: 1, minWidth: '200px' }}>
                            <div style={{ color: '#ffffff', fontWeight: '600', marginBottom: '4px' }}>
                              {customerName} – {bikeInfo}
                            </div>
                            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                              <Icon name="calendar" color="muted" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                              {new Date(b.booking_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                              {b.start_time && ` at ${(b.start_time + '').substring(0, 5)}`}
                            </div>
                            {serviceName && (
                              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '4px' }}>
                                {serviceName}
                              </div>
                            )}
                            <div style={{ fontSize: '12px', color: canDelegate ? 'rgba(255,255,255,0.6)' : '#ef4444', marginTop: '8px' }}>
                              {canDelegate ? (
                                <>Can delegate to: <strong>{techs.map(t => t.name).join(', ')}</strong></>
                              ) : (
                                <>No colleagues available for this slot</>
                              )}
                            </div>
                          </div>
                          
                          <div style={{ minWidth: canDelegate ? '180px' : '200px' }}>
                            {canDelegate ? (
                              <>
                                <label style={{ display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginBottom: '4px' }}>
                                  Delegate to:
                                </label>
                                <select
                                  value={delegationAssignments[b.id] || ''}
                                  onChange={(e) => setDelegationAssignments({
                                    ...delegationAssignments,
                                    [b.id]: e.target.value
                                  })}
                                  style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    background: darkMode ? 'rgba(255,255,255,0.1)' : '#ffffff',
                                    border: delegationAssignments[b.id] ? '1px solid rgba(74, 140, 24, 0.5)' : '1px solid rgba(245, 158, 11, 0.5)',
                                    color: darkMode ? '#ffffff' : '#1a1a1a',
                                    fontSize: '13px'
                                  }}
                                >
                                  <option value="">Select colleague...</option>
                                  {techs.map(tech => (
                                    <option key={tech.id} value={tech.id}>{tech.name}</option>
                                  ))}
                                </select>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleSuggestAlternativeDate(item)}
                                disabled={loadingAlternativeSlots}
                                className="btn btn-secondary"
                                style={{ whiteSpace: 'nowrap', fontSize: '13px' }}
                              >
                                {loadingAlternativeSlots && rescheduleForItem?.booking.id === b.id ? 'Loading...' : (
                                  <>
                                    <Icon name="calendar" color="primary" darkMode={darkMode} size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                    Suggest alternative date
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button 
                    className="btn btn-primary"
                    onClick={handleSubmitWithDelegations}
                    disabled={savingTimeOff}
                  >
                    {savingTimeOff ? 'Saving...' : (
                      <>
                        <Icon name="success" customColor="#ffffff" size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        Confirm & Send Delegation Requests
                      </>
                    )}
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowDelegationForm(false);
                      setBookingsToDelegate([]);
                      setDelegationAssignments({});
                    }}
                    disabled={savingTimeOff}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Reschedule modal – when no colleague available, suggest alternative date */}
            {showRescheduleModal && rescheduleForItem && (
              <div style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px'
              }} onClick={() => !loadingAlternativeSlots && !rescheduling && setShowRescheduleModal(false)}>
                <div style={{
                  background: darkMode ? '#1a1a2e' : '#ffffff',
                  borderRadius: '16px',
                  padding: '24px',
                  maxWidth: '480px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflow: 'auto',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                }} onClick={e => e.stopPropagation()}>
                  <h3 style={{ margin: '0 0 8px 0', color: darkMode ? '#fff' : '#1a1a1a', fontSize: '18px' }}>
                    Suggest alternative date
                  </h3>
                  <p style={{ margin: '0 0 16px 0', color: darkMode ? 'rgba(255,255,255,0.7)' : '#555', fontSize: '14px' }}>
                    The customer will be notified by email with the new date/time and bike shop contact. If it doesn&apos;t suit them, they can call the shop.
                  </p>
                  {loadingAlternativeSlots ? (
                    <div className="loading">Loading alternative slots...</div>
                  ) : alternativeSlots.length === 0 ? (
                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>No alternative slots in the next 14 days.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px', maxHeight: '280px', overflow: 'auto' }}>
                      {alternativeSlots.map((slot, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setSelectedSlot(slot)}
                          style={{
                            padding: '12px 16px',
                            borderRadius: '10px',
                            border: selectedSlot === slot ? '2px solid #3d5a20' : '1px solid rgba(255,255,255,0.15)',
                            background: selectedSlot === slot ? 'rgba(61, 90, 32, 0.2)' : (darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
                            color: darkMode ? '#fff' : '#1a1a1a',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          {new Date(slot.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} at {slot.start_time}
                          {slot.technician_name && ` – ${slot.technician_name}`}
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowRescheduleModal(false);
                        setRescheduleForItem(null);
                        setAlternativeSlots([]);
                        setSelectedSlot(null);
                      }}
                      disabled={rescheduling}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleConfirmReschedule}
                      disabled={!selectedSlot || rescheduling}
                    >
                      {rescheduling ? 'Rescheduling...' : 'Reschedule & notify customer'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Time Off List */}
            <h3 style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px' }}>Scheduled Time Off</h3>
            {loadingTimeOff ? (
              <div className="loading">Loading...</div>
            ) : timeOffList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255,255,255,0.5)' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
                <p>No time off scheduled</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {timeOffList.map(timeOff => {
                  const isPast = new Date(timeOff.end_date) < new Date();
                  const isOngoing = new Date(timeOff.start_date) <= new Date() && new Date(timeOff.end_date) >= new Date();
                  return (
                    <div 
                      key={timeOff.id}
                      style={{
                        background: isOngoing ? 'rgba(16, 185, 129, 0.15)' : isPast ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.05)',
                        border: isOngoing ? '2px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '12px',
                        padding: '16px',
                        opacity: isPast ? 0.6 : 1
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '16px' }}>
                              {timeOff.reason === 'vacation' ? '🏖️' : 
                               timeOff.reason === 'sick' ? '🤒' : 
                               timeOff.reason === 'personal' ? '👤' : 
                               timeOff.reason === 'training' ? '📚' : '📝'}
                            </span>
                            <span style={{ fontWeight: '600', color: '#ffffff', fontSize: '15px' }}>
                              {timeOff.reason?.charAt(0).toUpperCase() + timeOff.reason?.slice(1)}
                            </span>
                            {isOngoing && (
                              <span style={{
                                padding: '2px 8px',
                                background: 'rgba(26, 40, 17, 0.2)',
                                color: '#4A8C18',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '600'
                              }}>
                                ACTIVE
                              </span>
                            )}
                          </div>
                          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                            {new Date(timeOff.start_date).toLocaleDateString('en-GB', { 
                              weekday: 'short', 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                            {' — '}
                            {new Date(timeOff.end_date).toLocaleDateString('en-GB', { 
                              weekday: 'short', 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </div>
                        </div>
                        {!isPast && (
                          <button
                            onClick={() => handleDeleteTimeOff(timeOff.id)}
                            style={{
                              padding: '6px 12px',
                              background: 'rgba(239, 68, 68, 0.15)',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              color: '#ef4444',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
        <div className="card">
          <h2 style={{ fontSize: '18px', marginBottom: '24px', color: '#ffffff' }}>👤 Profile Settings</h2>
          
          <form onSubmit={handleProfileSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
              <div className="form-group">
                <label style={profileLabelStyle}>First Name</label>
                <input
                  type="text"
                  value={profileData.firstname}
                  onChange={(e) => setProfileData({...profileData, firstname: e.target.value})}
                  placeholder="First name"
                  style={profileInputStyle}
                />
              </div>
              <div className="form-group">
                <label style={profileLabelStyle}>Last Name</label>
                <input
                  type="text"
                  value={profileData.surname}
                  onChange={(e) => setProfileData({...profileData, surname: e.target.value})}
                  placeholder="Last name"
                  style={profileInputStyle}
                />
              </div>
            </div>

            <div className="form-group">
              <label style={profileLabelStyle}>Email</label>
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                placeholder="Email address"
                style={profileInputStyle}
              />
            </div>

            <div className="form-group">
              <label style={profileLabelStyle}>Phone</label>
              <input
                type="tel"
                value={profileData.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9+\-\s()]/g, '');
                  setProfileData({...profileData, phone: value});
                }}
                placeholder="+36 30 465 4535"
                style={profileInputStyle}
              />
            </div>

            {/* Signature Section */}
            <div style={{
              marginTop: '24px',
              padding: '20px',
              background: darkMode ? 'rgba(61, 90, 32, 0.1)' : 'rgba(61, 90, 32, 0.08)',
              border: `1px solid ${darkMode ? 'rgba(61, 90, 32, 0.2)' : 'rgba(61, 90, 32, 0.25)'}`,
              borderRadius: '12px'
            }}>
              <h3 style={{ 
                margin: '0 0 8px 0', 
                fontSize: '15px', 
                color: darkMode ? '#ffffff' : '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                ✍️ Personal signature
              </h3>
              <p style={{ 
                margin: '0 0 16px 0', 
                fontSize: '13px', 
                color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' 
              }}>
                Your signature will appear at the bottom of services you complete. 
                It adds a personal touch and signals professionalism.
              </p>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <textarea
                  value={profileData.signature}
                  onChange={(e) => setProfileData({...profileData, signature: e.target.value})}
                  placeholder="e.g., Regards, Alex Smith — Frame-Book technician"
                  rows={3}
                  maxLength={500}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #d1d5db',
                    background: darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
                    color: darkMode ? '#ffffff' : '#111827',
                    fontSize: '14px',
                    resize: 'vertical',
                    fontStyle: profileData.signature ? 'italic' : 'normal'
                  }}
                />
                <div style={{ 
                  fontSize: '11px', 
                  color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af',
                  marginTop: '8px',
                  textAlign: 'right'
                }}>
                  {profileData.signature?.length || 0} / 500
                </div>
              </div>
              {profileData.signature && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                  borderRadius: '8px',
                  borderLeft: '3px solid #3d5a20'
                }}>
                  <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af', marginBottom: '4px' }}>
                    Preview:
                  </div>
                  <div style={{ 
                    fontSize: '13px', 
                    fontStyle: 'italic', 
                    color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {profileData.signature}
                  </div>
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary" style={{ marginTop: '24px' }} disabled={savingProfile}>
              {savingProfile ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        </div>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
        <div className="card">
          <h2 style={{ fontSize: '18px', marginBottom: '24px', color: '#ffffff' }}>🔐 Change Password</h2>
          
          <form onSubmit={handlePasswordSubmit}>
            <div className="form-group">
              <label style={{ color: 'rgba(255,255,255,0.7)' }}>Current Password</label>
              <input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                required
                placeholder="Enter current password"
              />
            </div>

            <div className="form-group">
              <label style={{ color: 'rgba(255,255,255,0.7)' }}>New Password</label>
              <input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                required
                minLength={6}
                placeholder="Enter new password"
              />
            </div>

            <div className="form-group">
              <label style={{ color: 'rgba(255,255,255,0.7)' }}>Confirm New Password</label>
              <input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                required
                placeholder="Confirm new password"
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={savingPassword}>
              {savingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
        )}

        {/* Booking Details Modal */}
        {showBookingModal && selectedBooking && (
          <div className="modal-overlay" onClick={() => setShowBookingModal(false)}>
            <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Booking Details</h2>
                <button className="close-btn" onClick={() => setShowBookingModal(false)}>×</button>
              </div>
              
              <div style={{ padding: '20px' }}>
                {/* Status Badge */}
                <div style={{ marginBottom: '20px' }}>
                  <span style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    background: (STATUS_COLORS[selectedBooking.status] || STATUS_COLORS.pending).bg,
                    color: (STATUS_COLORS[selectedBooking.status] || STATUS_COLORS.pending).text,
                    textTransform: 'uppercase'
                  }}>
                    {selectedBooking.status?.replace('_', ' ')}
                  </span>
                </div>

                {/* Time & Date */}
                <div style={{ 
                  padding: '16px', 
                  background: 'var(--bg-overlay-light)', 
                  borderRadius: '12px', 
                  marginBottom: '16px' 
                }}>
                  <h3 style={{ color: '#4A8C18', fontSize: '14px', marginBottom: '12px' }}>📅 Schedule</h3>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    <div><strong>Date:</strong> {new Date(selectedBooking.booking_date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                    <div><strong>Time:</strong> {formatTime(selectedBooking.start_time)} - {formatTime(selectedBooking.end_time)}</div>
                    <div><strong>Service:</strong> {selectedBooking.service_template?.name || 'N/A'}</div>
                  </div>
                </div>

                {/* Bike Info */}
                {selectedBooking.bike && (
                  <div style={{ 
                    padding: '16px', 
                    background: 'var(--bg-overlay-light)', 
                    borderRadius: '12px', 
                    marginBottom: '16px' 
                  }}>
                    <h3 style={{ color: '#4A8C18', fontSize: '14px', marginBottom: '12px' }}>🚲 Bike Information</h3>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      <div><strong>Brand:</strong> {selectedBooking.bike.brand}</div>
                      <div><strong>Model:</strong> {selectedBooking.bike.model}</div>
                      <div><strong>Year:</strong> {selectedBooking.bike.year}</div>
                      <div><strong>Frame Number:</strong> {selectedBooking.bike.vin}</div>
                    </div>
                  </div>
                )}

                {/* Customer Info */}
                {selectedBooking.user && (
                  <div style={{ 
                    padding: '16px', 
                    background: 'var(--bg-overlay-light)', 
                    borderRadius: '12px', 
                    marginBottom: '16px' 
                  }}>
                    <h3 style={{ color: '#4A8C18', fontSize: '14px', marginBottom: '12px' }}>👤 Customer</h3>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      <div><strong>Name:</strong> {selectedBooking.user.firstname} {selectedBooking.user.surname}</div>
                      {selectedBooking.user.email && <div><strong>Email:</strong> {selectedBooking.user.email}</div>}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedBooking.notes && (
                  <div style={{ 
                    padding: '16px', 
                    background: 'var(--bg-overlay-light)', 
                    borderRadius: '12px', 
                    marginBottom: '16px' 
                  }}>
                    <h3 style={{ color: '#4A8C18', fontSize: '14px', marginBottom: '12px' }}>📝 Notes</h3>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{selectedBooking.notes}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '10px', marginTop: '24px', flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                  {/* Start Work: only for non-technician (e.g. company_admin viewing as technician); technicians save as draft with parts instead */}
                  {(selectedBooking.status === 'pending' || selectedBooking.status === 'confirmed') && user?.role !== 'technician' && (
                    <button 
                      className="btn btn-primary"
                      onClick={() => {
                        handleUpdateBookingStatus(selectedBooking, 'in_progress');
                        // Keep modal open to show service details
                      }}
                      disabled={updatingStatus}
                      style={{ background: '#4ecdc4' }}
                    >
                      {updatingStatus ? 'Updating...' : '▶️ Start Work'}
                    </button>
                  )}
                  {/* Technician: add parts from Square catalog/warehouse; save as draft only (no invoice creation) */}
                  {user?.role === 'technician' && (selectedBooking.status === 'pending' || selectedBooking.status === 'confirmed') && (
                    <>
                      <div style={{ width: '100%', marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '14px', fontWeight: '500' }}>
                          🔧 Felhasznált alkatrészek (Square raktárkészlet)
                        </label>
                        {squareCatalogLoading && (
                          <div style={{ padding: '12px', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', fontSize: '13px' }}>
                            Katalógus betöltése...
                          </div>
                        )}
                        {!squareCatalogLoading && squareCatalogError && (
                          <div style={{ padding: '8px', marginBottom: '8px', borderRadius: '8px', background: 'rgba(245,158,11,0.1)', color: '#f59e0b', fontSize: '12px' }}>
                            {squareCatalogError} Egyéni alkatrészeket tudsz még hozzáadni alul.
                          </div>
                        )}
                        {!squareCatalogLoading && squareCatalogItems.length > 0 && (
                          <>
                            <input
                              type="text"
                              placeholder="Keresés a katalógusban..."
                              value={catalogSearchTerm}
                              onChange={(e) => setCatalogSearchTerm(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                marginBottom: '10px',
                                borderRadius: '8px',
                                border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.2)',
                                background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                                color: darkMode ? '#fff' : '#1a1a1a',
                                fontSize: '14px'
                              }}
                            />
                            <div style={{ maxHeight: '200px', overflowY: 'auto', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)', borderRadius: '8px', marginBottom: '10px' }}>
                              {squareCatalogItems
                                .filter((item) => {
                                  const q = (catalogSearchTerm || '').trim().toLowerCase();
                                  if (!q) return true;
                                  const name = (item.name || item.item_name || '').toLowerCase();
                                  const sku = (item.sku || '').toLowerCase();
                                  return name.includes(q) || sku.includes(q);
                                })
                                .map((item) => {
                                  const stock = Number(squareInventory[item.variation_id] ?? 0);
                                  const unitPrice = item.price != null ? item.price : (item.price_cents != null ? item.price_cents / 100 : 0);
                                  return (
                                    <div
                                      key={item.variation_id}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '8px 12px',
                                        borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                                        gap: '8px',
                                        flexWrap: 'wrap'
                                      }}
                                    >
                                      <div style={{ flex: 1, minWidth: '140px' }}>
                                        <span style={{ fontWeight: '500', color: darkMode ? '#fff' : '#1a1a1a' }}>{item.name || item.item_name}</span>
                                        <span style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', marginLeft: '8px' }}>
                                          Készlet: {stock}
                                        </span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <input
                                          type="number"
                                          min="1"
                                          max={stock > 0 ? stock : 999}
                                          defaultValue={1}
                                          id={`qty-${item.variation_id}`}
                                          style={{
                                            width: '56px',
                                            padding: '6px 8px',
                                            borderRadius: '6px',
                                            border: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                            background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                                            color: darkMode ? '#fff' : '#1a1a1a',
                                            fontSize: '13px'
                                          }}
                                        />
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const input = document.getElementById(`qty-${item.variation_id}`);
                                            const qty = Math.max(1, parseInt(input?.value || '1', 10) || 1);
                                            setDraftParts((prev) => [...prev, {
                                              item_name: item.name || item.item_name,
                                              quantity: qty,
                                              unit_price: unitPrice,
                                              variation_id: item.variation_id || '',
                                              catalog_object_id: item.variation_id || '',
                                            }]);
                                          }}
                                          style={{
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            border: '1px solid rgba(74, 140, 24, 0.5)',
                                            background: 'rgba(74, 140, 24, 0.15)',
                                            color: '#4A8C18',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                          }}
                                        >
                                          Hozzáad
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </>
                        )}
                        <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)' }}>
                          Kiválasztott alkatrészek
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                          {draftParts.filter((p) => (p.item_name || '').trim()).map((part, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ flex: 1, minWidth: '100px', fontSize: '14px' }}>{part.item_name}</span>
                              <span style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>Qty: {Number(part.quantity)}</span>
                              <button
                                type="button"
                                onClick={() => setDraftParts((prev) => prev.filter((_, i) => i !== idx))}
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  border: '1px solid rgba(239,68,68,0.4)',
                                  background: 'rgba(239,68,68,0.1)',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  fontSize: '11px'
                                }}
                              >
                                Törlés
                              </button>
                            </div>
                          ))}
                          {draftParts.filter((p) => (p.item_name || '').trim()).length === 0 && (
                            <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>
                              Nincs kiválasztott alkatrész. Fent a katalógusból válassz, vagy add hozzá egyéni sort.
                            </div>
                          )}
                          {/* Manual row: one empty row for custom part */}
                          {draftParts.some((p) => !(p.item_name || '').trim()) ? (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <input
                                type="text"
                                placeholder="Egyéni alkatrész neve"
                                value={draftParts.find((p) => !(p.item_name || '').trim())?.item_name || ''}
                                onChange={(e) => {
                                  const emptyIdx = draftParts.findIndex((p) => !(p.item_name || '').trim());
                                  const next = [...draftParts];
                                  if (emptyIdx >= 0) next[emptyIdx] = { ...next[emptyIdx], item_name: e.target.value };
                                  else next.push({ item_name: e.target.value, quantity: 1, unit_price: 0, variation_id: '', catalog_object_id: '' });
                                  setDraftParts(next);
                                }}
                                style={{
                                  flex: 1,
                                  minWidth: '120px',
                                  padding: '8px 12px',
                                  borderRadius: '8px',
                                  border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.2)',
                                  background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                                  color: darkMode ? '#fff' : '#1a1a1a',
                                  fontSize: '14px'
                                }}
                              />
                              <input
                                type="number"
                                min="0"
                                step="1"
                                placeholder="Db"
                                value={draftParts.find((p) => !(p.item_name || '').trim())?.quantity ?? 1}
                                onChange={(e) => {
                                  const emptyIdx = draftParts.findIndex((p) => !(p.item_name || '').trim());
                                  const next = [...draftParts];
                                  const val = parseFloat(e.target.value) || 0;
                                  if (emptyIdx >= 0) next[emptyIdx] = { ...next[emptyIdx], quantity: val };
                                  setDraftParts(next);
                                }}
                                style={{
                                  width: '60px',
                                  padding: '8px',
                                  borderRadius: '8px',
                                  border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.2)',
                                  background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                                  color: darkMode ? '#fff' : '#1a1a1a',
                                  fontSize: '14px'
                                }}
                              />
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setDraftParts((prev) => [...prev, { item_name: '', quantity: 1, unit_price: 0, variation_id: '', catalog_object_id: '' }])}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '8px',
                                border: darkMode ? '1px dashed rgba(255,255,255,0.3)' : '1px dashed rgba(0,0,0,0.3)',
                                background: 'transparent',
                                color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                                cursor: 'pointer',
                                fontSize: '12px',
                                alignSelf: 'flex-start'
                              }}
                            >
                              + Egyéni alkatrész
                            </button>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', marginTop: '6px' }}>
                          Csak piszkozat: a számlát a cég adminja készíti el (nem tudsz végleges számlát létrehozni).
                        </div>
                      </div>
                      <div style={{ marginBottom: '12px', width: '100%' }}>
                        <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '13px' }}>Megjegyzés (opcionális)</label>
                        <textarea
                          value={completionNotes}
                          onChange={(e) => setCompletionNotes(e.target.value)}
                          placeholder="Rövid megjegyzés a munkához..."
                          rows={2}
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '8px',
                            border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.2)',
                            background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                            color: darkMode ? '#fff' : '#1a1a1a',
                            fontSize: '14px',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                      <button
                        className="btn btn-primary"
                        onClick={handleSaveDraft}
                        disabled={savingDraft}
                        style={{ background: '#4A8C18' }}
                      >
                        {savingDraft ? 'Mentés...' : '💾 Piszkozat mentése (számlázás később)'}
                      </button>
                    </>
                  )}
                  {selectedBooking.status === 'in_progress' && (
                    <>
                      <div style={{ marginBottom: '16px', width: '100%' }}>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '8px', 
                          color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', 
                          fontSize: '14px',
                          fontWeight: '500'
                        }}>
                          📝 Service Notes (Optional)
                        </label>
                        <textarea
                          value={completionNotes}
                          onChange={(e) => setCompletionNotes(e.target.value)}
                          placeholder="Enter notes about what was done, serial number, parts replaced, etc..."
                          rows={4}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '10px',
                            border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.2)',
                            background: darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
                            color: darkMode ? '#ffffff' : '#1a1a1a',
                            fontSize: '14px',
                            resize: 'vertical',
                            fontFamily: 'inherit'
                          }}
                        />
                        <div style={{ 
                          fontSize: '12px', 
                          color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                          marginTop: '6px'
                        }}>
                          Document what was done, parts replaced, serial numbers, etc.
                        </div>
                      </div>
                      
                      {/* Confirmation dialog */}
                      {showCompleteConfirmation ? (
                        <div style={{
                          background: darkMode ? 'rgba(74, 140, 24, 0.1)' : 'rgba(74, 140, 24, 0.05)',
                          border: '1px solid rgba(74, 140, 24, 0.3)',
                          borderRadius: '12px',
                          padding: '16px',
                          width: '100%'
                        }}>
                          <p style={{ 
                            color: darkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)', 
                            marginBottom: '12px',
                            fontSize: '14px'
                          }}>
                            <Icon name="info" color="warning" darkMode={darkMode} size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Are you sure this service is complete? The customer will be notified by email.
                          </p>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button 
                              className="btn btn-primary"
                              onClick={() => handleUpdateBookingStatus(selectedBooking, 'completed')}
                              disabled={updatingStatus}
                              style={{ flex: 1 }}
                            >
                              {updatingStatus ? 'Completing...' : (
                                <>
                                  <Icon name="success" customColor="#ffffff" size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                                  Yes, Complete & Notify
                                </>
                              )}
                            </button>
                            <button 
                              className="btn btn-secondary"
                              onClick={() => setShowCompleteConfirmation(false)}
                              disabled={updatingStatus}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button 
                          className="btn btn-primary"
                          onClick={() => setShowCompleteConfirmation(true)}
                          disabled={updatingStatus}
                          style={{ width: '100%' }}
                        >
                          <Icon name="success" customColor="#ffffff" size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                          Service Completed
                        </button>
                      )}
                    </>
                  )}
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setShowBookingModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Completed Bookings Modal */}
        {showCompletedModal && (
          <div className="modal-overlay" onClick={() => setShowCompletedModal(false)}>
            <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Completed This Week</h2>
                <button className="close-btn" onClick={() => setShowCompletedModal(false)}>×</button>
              </div>
              
              <div style={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto' }}>
                {completedBookingsList.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255,255,255,0.5)' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <Icon name="success" color="success" darkMode={true} size={48} />
                    </div>
                    <p>No completed bookings this week</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {completedBookingsList.map(booking => (
                      <div
                        key={booking.id}
                        onClick={() => {
                          setSelectedBooking(booking);
                          setShowCompletedModal(false);
                          setShowBookingModal(true);
                        }}
                        style={{
                          padding: '16px',
                          background: 'var(--bg-overlay-light)',
                          borderRadius: '12px',
                          border: '1px solid rgba(255,255,255,0.06)',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(61, 90, 32, 0.1)';
                          e.currentTarget.style.borderColor = 'rgba(61, 90, 32, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'var(--bg-overlay-light)';
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <div>
                            <div style={{ fontWeight: '600', color: '#ffffff', fontSize: '15px', marginBottom: '4px' }}>
                              {new Date(booking.booking_date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                            </div>
                            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                              {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                            </div>
                          </div>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            background: STATUS_COLORS.completed.bg,
                            color: STATUS_COLORS.completed.text,
                            textTransform: 'uppercase'
                          }}>
                            Completed
                          </span>
                        </div>
                        {booking.service_template && (
                          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', marginBottom: '4px' }}>
                            🔧 {booking.service_template.name}
                          </div>
                        )}
                        {booking.bike && (
                          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                            🚲 {booking.bike.brand} {booking.bike.model} {booking.bike.year && `(${booking.bike.year})`}
                          </div>
                        )}
                        {booking.user && (
                          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginTop: '4px' }}>
                            👤 {booking.user.firstname} {booking.user.surname}
                            {booking.user.email && ` • ${booking.user.email}`}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Quick Book Slot Modal */}
        {showQuickBookModal && quickBookSlot && (
          <div className="modal-overlay" onClick={() => setShowQuickBookModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px', width: '95%', margin: '0 auto' }}>
              <div className="modal-header">
                <h2>📅 Add Booking</h2>
                <button className="close-btn" onClick={() => setShowQuickBookModal(false)}>×</button>
              </div>
              
              <div style={{ padding: '20px' }}>
                <div style={{
                  background: 'rgba(61, 90, 32, 0.1)',
                  border: '1px solid rgba(61, 90, 32, 0.2)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ fontSize: '32px' }}>🗓️</div>
                    <div>
                      <div style={{ fontWeight: '600', color: '#ffffff', fontSize: '16px' }}>
                        {quickBookSlot.date.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                        ⏰ {quickBookSlot.time}
                      </div>
                    </div>
                  </div>
                </div>
                
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '20px' }}>
                  If you're on the phone with a customer and want to book an appointment, ask an admin colleague to create the booking, 
                  or direct the customer to the online booking interface.
                </p>
                
                <div style={{ 
                  background: 'rgba(59, 130, 246, 0.1)', 
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>
                    💡 Tip:
                  </div>
                  <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                    Note down the time slot ({quickBookSlot.date.toLocaleDateString('en-GB')} {quickBookSlot.time}) and 
                    the customer details, then pass it to the admin.
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setShowQuickBookModal(false)}
                    style={{ flex: '1 1 120px', minWidth: '120px' }}
                  >
                    Close
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      // Copy time slot info to clipboard
                      const info = `Booking: ${quickBookSlot.date.toLocaleDateString('en-GB')} ${quickBookSlot.time}`;
                      navigator.clipboard.writeText(info).then(() => {
                        setSuccess('Time slot copied to clipboard!');
                        setShowQuickBookModal(false);
                        setTimeout(() => setSuccess(''), 3000);
                      }).catch(() => {
                        setSuccess('Time slot: ' + info);
                        setShowQuickBookModal(false);
                        setTimeout(() => setSuccess(''), 5000);
                      });
                    }}
                    style={{ flex: '1 1 120px', minWidth: '120px' }}
                  >
                    📋 Copy
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default TechnicianDashboard;
